"use client";

import { useState, useEffect } from "react";
import { Search, X } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { useT } from "@/hooks/useT";

interface DegenboxMarket {
  id: string;
  slug: string;
  eventSlug?: string | null;
  title: string;
  image: string | null;
  yesPrice: number;
  noPrice: number;
  volume24hr: number;
  view: "orderbook" | "trades" | "chart";
  type: "market" | "event";
  markets?: any[];
  clobTokenIds?: string[];
}

interface MarketSearchModalProps {
  onClose: () => void;
  onSelect: (market: DegenboxMarket) => void;
  existingMarkets: string[];
}

export default function MarketSearchModal({
  onClose,
  onSelect,
  existingMarkets,
}: MarketSearchModalProps) {
  const t = useT();
  const [search, setSearch] = useState("");
  const [markets, setMarkets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<"events" | "markets">("markets");

  useEffect(() => {
    fetchMarkets();
  }, []);

  async function fetchMarkets() {
    try {
      // closed=false drops resolved events on the gamma side; we still
      // double-check client-side because gamma is occasionally slow to
      // mark recently-resolved events.
      const response = await authFetch("/api/polymarket/markets?limit=50&active=true&closed=false");
      if (!response.ok) throw new Error("Failed to fetch markets");

      const data = await response.json();
      setMarkets(data.events || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  // Drop events/markets that already resolved or whose endDate is in the
  // past — gamma's `active=true` flag isn't always set in lockstep with
  // resolution, so old "Will the US x Iran ceasefire be extended by April
  // 14, 2026?" rows leak through.
  const nowMs = Date.now();
  const isOpenEvent = (e: any) => {
    if (e?.closed) return false;
    if (e?.endDate && new Date(e.endDate).getTime() < nowMs) return false;
    return true;
  };
  const isOpenMarket = (m: any) => {
    if (m?.closed) return false;
    if (m?.endDate && new Date(m.endDate).getTime() < nowMs) return false;
    return true;
  };

  // Filter based on view mode
  const filteredEvents = markets.filter((event) =>
    isOpenEvent(event) &&
    event.title.toLowerCase().includes(search.toLowerCase()) &&
    !existingMarkets.includes(event.id)
  );

  // Flatten events into individual markets
  const allMarkets = markets
    .filter(isOpenEvent)
    .flatMap((event) =>
      event.markets.filter(isOpenMarket).map((market: any) => ({
        ...market,
        eventTitle: event.title,
        eventImage: event.image,
        eventVolume24hr: event.volume24hr,
        eventSlug: event.slug,
      }))
    );

  const filteredMarkets = allMarkets.filter((market) =>
    (market.question?.toLowerCase().includes(search.toLowerCase()) ||
     market.eventTitle?.toLowerCase().includes(search.toLowerCase())) &&
    !existingMarkets.includes(market.id)
  );

  // Polymarket gamma-api sometimes returns outcomePrices as a JSON
  // string ('["0.5","0.5"]') instead of an array. `arr?.[0]` on the
  // string returns "[" which is truthy but coerces to NaN, so prices
  // either save as the literal "[" character or fall back to 0.5
  // everywhere → all DegenBox tiles read "NaN¢" or "50¢" regardless
  // of the real market state. Parse defensively before storing.
  const parsePrices = (raw: any): [number, number] => {
    if (Array.isArray(raw)) {
      return [Number(raw[0]) || 0.5, Number(raw[1]) || 0.5];
    }
    if (typeof raw === 'string') {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          return [Number(arr[0]) || 0.5, Number(arr[1]) || 0.5];
        }
      } catch {
        /* fall through */
      }
    }
    return [0.5, 0.5];
  };

  // gamma serves clobTokenIds as a JSON-encoded string ('["123","456"]')
  // for some endpoints; saving the raw string into the DegenBox slot
  // breaks the chart fetch (string.length is truthy, so the parse-on-fetch
  // path is skipped, and indexing returns "[" instead of a real token).
  const parseClobTokenIds = (raw: any): string[] => {
    if (Array.isArray(raw)) return raw.filter((x) => typeof x === 'string');
    if (typeof raw === 'string') {
      try {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) return arr.filter((x) => typeof x === 'string');
      } catch { /* fall through */ }
      // Comma-separated form occasionally seen on older gamma responses
      if (raw.includes(',')) return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const handleSelectEvent = (event: any) => {
    const mainMarket = event.markets[0];
    if (!mainMarket) return;

    const [yesPrice, noPrice] = parsePrices(mainMarket.outcomePrices);
    const selectedEvent = {
      id: event.id,
      slug: event.slug,
      eventSlug: event.slug,
      title: event.title,
      image: event.image,
      yesPrice,
      noPrice,
      volume24hr: event.volume24hr || 0,
      view: "orderbook",
      type: "event",
      markets: event.markets,
      clobTokenIds: parseClobTokenIds(mainMarket.clobTokenIds),
    } as DegenboxMarket;

    onSelect(selectedEvent);
    onClose();
  };

  const handleSelectMarket = (market: any) => {
    const [yesPrice, noPrice] = parsePrices(market.outcomePrices);
    const selectedMarket = {
      id: market.id,
      slug: market.slug,
      eventSlug: market.eventSlug || null,
      title: market.question || market.eventTitle,
      image: market.eventImage,
      yesPrice,
      noPrice,
      volume24hr: market.eventVolume24hr || 0,
      view: "orderbook",
      type: "market",
      clobTokenIds: parseClobTokenIds(market.clobTokenIds),
    } as DegenboxMarket;

    onSelect(selectedMarket);
    onClose();
  };

  const displayList = viewMode === "events" ? filteredEvents : filteredMarkets;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] w-full max-w-2xl max-h-[80vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-[#1A1A1A] flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {t("degen.search.title")}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-[5px] transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        {/* Tabs */}
        <div className="px-6 pt-4 border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex gap-2 mb-4">
            <button
              onClick={() => setViewMode("markets")}
              className={`px-4 py-2 rounded-[5px] text-sm font-semibold transition-colors ${
                viewMode === "markets"
                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                  : "bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {t("degen.search.tab.markets")}
            </button>
            <button
              onClick={() => setViewMode("events")}
              className={`px-4 py-2 rounded-[5px] text-sm font-semibold transition-colors ${
                viewMode === "events"
                  ? "bg-gray-900 dark:bg-white text-white dark:text-gray-900"
                  : "bg-gray-100 dark:bg-white/[0.04] text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {t("degen.search.tab.events", { n: markets.length })}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-6 border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <Search className="w-4 h-4" />
            </div>
            <input
              type="text"
              placeholder={viewMode === "markets" ? t("degen.search.placeholder.markets") : t("degen.search.placeholder.events")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-11 pr-4 py-2.5 bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-gray-400 dark:focus:border-white/20 hover:border-gray-300 dark:hover:border-white/10 transition-colors"
              autoFocus
            />
          </div>
        </div>

        {/* Results */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-6 h-6 border-2 border-gray-300 dark:border-gray-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : displayList.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {search ? t("degen.search.noResults", { kind: t(`degen.search.kind.${viewMode}`) }) : t("degen.search.noAvailable", { kind: t(`degen.search.kind.${viewMode}`) })}
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {viewMode === "events" ? (
                /* EVENTS VIEW */
                displayList.map((event: any) => {
                  return (
                    <button
                      key={event.id}
                      onClick={() => handleSelectEvent(event)}
                      className="w-full p-4 bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-white/[0.1] rounded-[5px] transition-colors text-left flex items-center gap-4"
                    >
                      {event.image && (
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-10 h-10 rounded-[5px] object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1 line-clamp-2">
                          {event.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-gray-900 dark:text-white font-semibold">
                            {t("degen.search.marketsCount", { n: event.markets.length })}
                          </span>
                          <span className="text-gray-400">
                            ${event.volume24hr
                              ? event.volume24hr >= 1000000
                                ? `${(event.volume24hr / 1000000).toFixed(1)}M`
                                : `${(event.volume24hr / 1000).toFixed(1)}K`
                              : "0"} {t("degen.search.vol")}
                          </span>
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                /* MARKETS VIEW */
                displayList.map((market: any) => (
                  <button
                    key={market.id}
                    onClick={() => handleSelectMarket(market)}
                    className="w-full p-4 bg-gray-50 dark:bg-white/[0.02] border border-gray-200 dark:border-[#1A1A1A] hover:border-gray-300 dark:hover:border-white/[0.1] rounded-[5px] transition-colors text-left flex items-center gap-4"
                  >
                    {market.eventImage && (
                      <img
                        src={market.eventImage}
                        alt={market.question || market.eventTitle}
                        className="w-10 h-10 rounded-[5px] object-cover flex-shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1 line-clamp-2">
                        {market.question || market.eventTitle}
                      </h3>
                      {(() => {
                        const [yes, no] = parsePrices(market.outcomePrices);
                        return (
                          <div className="flex items-center gap-3 text-xs">
                            <span className="text-emerald-500 font-semibold tabular-nums">
                              {t("bub.label.yes")} {(yes * 100).toFixed(1)}¢
                            </span>
                            <span className="text-red-400 font-semibold tabular-nums">
                              {t("bub.label.no")} {(no * 100).toFixed(1)}¢
                            </span>
                          </div>
                        );
                      })()}
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
